import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Send, Loader2, ShieldCheck, BookOpenCheck, Stethoscope, Sparkles,
  AlertTriangle, RefreshCw, ArrowRight, MessageSquareHeart,
} from 'lucide-react';
import { API } from '../utils/authAxios';

const TITLE = 'TerapeutIA — IA especializada en tratamiento de adicciones | SinAdicciones.org';
const DESC = 'Chatea con TerapeutIA, asistente de IA enfocada solo en adicciones, tratamientos y rehabilitación en Chile. Información basada en SENDA, OMS, OPS, NIDA y nuestro estudio Reto 21. No reemplaza terapeuta ni entrega diagnóstico.';

const QUICK_PROMPTS = [
  { id: 'soy-adicto', label: '¿Cómo saber si soy adicto?', icon: '🧭' },
  { id: 'cuanto-cuesta', label: '¿Cuánto cuesta un tratamiento?', icon: '💰' },
  { id: 'tipos', label: '¿Qué tipos de tratamientos hay?', icon: '🏥' },
  { id: 'familiar', label: '¿Cómo ayudar a un familiar?', icon: '🤝' },
];

const SOURCES = ['SENDA', 'Minsal', 'OMS', 'OPS', 'NIDA', 'APA', 'Estudio Reto 21'];

// FAQ schema (JSON-LD) — boost SEO con rich snippets para los quick prompts
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '¿Cómo saber si soy adicto?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Señales orientativas de un problema de consumo: pérdida de control (consumir más o por más tiempo de lo que pretendías), tolerancia (necesitar más cantidad para el mismo efecto), síndrome de abstinencia al no consumir, vida social/laboral/familiar afectada e intentos fallidos de dejar o reducir. Si reconoces 3 o más, se recomienda evaluación profesional. En SinAdicciones.org puedes hacer tests gratuitos validados por OMS y SENDA (AUDIT, DAST, URICA) en /herramientas, o conversar con TerapeutIA, nuestra IA especializada solo en adicciones. Esta información es orientativa y no constituye diagnóstico clínico.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Cuánto cuesta un tratamiento en adicciones en Chile?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Rangos reales 2026 en Chile: tratamiento residencial privado entre $280.000 y $1.500.000 CLP al mes con duración típica de 7 a 12 meses; residencial vía SENDA gratuito o copago bajo ($0 a $200.000); ambulatorio entre $350.000 y $700.000 al mes con duración de 6 a 12 meses; tratamiento online entre $200.000 y $500.000 al mes con duración de 3 a 12 meses; sesiones individuales con psicólogo $25.000-$60.000 y con psiquiatra $80.000-$180.000. El precio no siempre refleja calidad: hay centros verificados accesibles con excelente equipo interdisciplinario. Para una cotización real ajustada a tu caso, presupuesto y región, usa nuestro cotizador gratis en /cotizar y recibirás propuestas de centros verificados.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Qué tipos de tratamientos para adicciones existen?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'En Chile existen cinco modalidades principales: 1) Residencial o internación (24h con programa estructurado, ideal para consumo diario o patología dual severa, duración típica 7-12 meses); 2) Ambulatorio intensivo / PHP-IOP (4-6 horas diarias varios días por semana, mantiene rutina); 3) Ambulatorio regular (1-2 sesiones semanales con psicólogo o psiquiatra, duración 6-12 meses); 4) Tratamiento online o telepsicología (útil con barreras geográficas o severidad leve a moderada, duración 3-12 meses); 5) Comunidad terapéutica de larga estadía (6-12 meses, perfiles más crónicos). Como complemento, los grupos AA y NA son gratuitos y muy efectivos. Explora opciones reales en /centros.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Cómo ayudar a un familiar con adicción?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Cinco claves para acompañar sin co-dependencia: no discutir cuando la persona está intoxicada; establecer límites claros y no rescatarla de las consecuencias de su consumo; buscar apoyo para ti en grupos como Al-Anon o Nar-Anon (gratuitos y presenciales/online); involucrar a un profesional para una intervención efectiva; nunca forzar una internación sin acompañamiento profesional, salvo riesgo vital. La paciencia con las recaídas es clave: el 76% de quienes piden ayuda ya lo intentaron antes (Estudio Reto 21, SinAdicciones.org). En /herramientas tienes el Test de Riesgo Familiar y el Test de Codependencia, ambos gratuitos.',
      },
    },
  ],
};

function newSessionId() {
  return 'terapeutia-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

function useSeo() {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', DESC);
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESC, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', 'https://sinadicciones.org/terapeutia', 'property');
    setMeta('og:image', 'https://sinadicciones.org/api/terapeutia/hero-image', 'property');
    setMeta('twitter:card', 'summary_large_image');

    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', 'https://sinadicciones.org/terapeutia');

    // FAQ JSON-LD (rich snippets para Google)
    const SCHEMA_ID = 'terapeutia-faq-schema';
    let script = document.getElementById(SCHEMA_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCHEMA_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(FAQ_SCHEMA);

    return () => {
      const s = document.getElementById(SCHEMA_ID);
      if (s) s.remove();
    };
  }, []);
}

// Render markdown-lite (headings, links, **bold**, line breaks)
function renderMessage(content) {
  const lines = content.split('\n');
  return lines.map((line, lineIdx) => {
    // Headings: ### / ## / # (treat as bold paragraph)
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const sizeClass = level === 1 ? 'text-base font-bold' : level === 2 ? 'text-sm font-bold' : 'text-sm font-semibold';
      return (
        <div key={lineIdx} className={`${sizeClass} text-slate-900 mt-2 mb-1 first:mt-0`}>
          {renderInline(text, lineIdx)}
        </div>
      );
    }
    // Bullets
    const bulletMatch = line.match(/^[\s]*[-•*]\s+(.+)$/);
    if (bulletMatch) {
      return (
        <div key={lineIdx} className="flex gap-2 ml-1">
          <span className="text-emerald-500 shrink-0">•</span>
          <span>{renderInline(bulletMatch[1], lineIdx)}</span>
        </div>
      );
    }
    // Empty line
    if (line.trim() === '') return <div key={lineIdx} className="h-2" />;
    // Regular line
    return <div key={lineIdx}>{renderInline(line, lineIdx)}</div>;
  });
}

function renderInline(text, baseKey) {
  // Acepta opcional espacio en blanco entre ] y ( para tolerar el formateo de Claude
  const parts = text.split(/(\[[^\]]+\]\s*\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    const linkMatch = p.match(/^\[([^\]]+)\]\s*\(([^)]+)\)$/);
    if (linkMatch) {
      const url = linkMatch[2];
      const internal = url.startsWith('/');
      return internal ? (
        <Link
          key={`${baseKey}-${i}`}
          to={url}
          className="text-emerald-600 underline font-medium hover:text-emerald-700"
          data-testid={`chat-link-${baseKey}-${i}`}
        >
          {linkMatch[1]}
        </Link>
      ) : (
        <a
          key={`${baseKey}-${i}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 underline font-medium hover:text-emerald-700"
        >
          {linkMatch[1]}
        </a>
      );
    }
    const boldMatch = p.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      // Re-parsear contenido del bold para detectar links anidados (**[texto](/url)**)
      const inner = boldMatch[1];
      const innerLink = inner.match(/^\[([^\]]+)\]\s*\(([^)]+)\)$/);
      if (innerLink) {
        const url = innerLink[2];
        const internal = url.startsWith('/');
        const linkProps = {
          className: "text-emerald-600 underline font-bold hover:text-emerald-700",
        };
        return internal ? (
          <Link key={`${baseKey}-${i}`} to={url} {...linkProps} data-testid={`chat-link-${baseKey}-${i}`}>{innerLink[1]}</Link>
        ) : (
          <a key={`${baseKey}-${i}`} href={url} target="_blank" rel="noopener noreferrer" {...linkProps}>{innerLink[1]}</a>
        );
      }
      return <strong key={`${baseKey}-${i}`} className="font-semibold text-slate-900">{inner}</strong>;
    }
    const italicMatch = p.match(/^\*([^*]+)\*$/);
    if (italicMatch) return <em key={`${baseKey}-${i}`} className="italic">{italicMatch[1]}</em>;
    return <span key={`${baseKey}-${i}`}>{p}</span>;
  });
}

const WELCOME = `¡Hola! Soy **TerapeutIA**, asistente de IA enfocada únicamente en adicciones y tratamientos en Chile. Estoy aquí para orientarte con información basada en fuentes confiables (SENDA, OMS, OPS, NIDA, APA y nuestro estudio interno de 1.023 chilenos del Reto 21 Días).

**Importante**: no reemplazo a un terapeuta ni te puedo dar un diagnóstico clínico. Pero sí puedo ayudarte a entender qué te está pasando, qué opciones de tratamiento existen y derivarte al recurso correcto.

¿Por qué tema te gustaría empezar?`;

export default function TerapeutIAPage() {
  useSeo();
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestCotizar, setSuggestCotizar] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, loading]);

  async function sendPrompt(text) {
    const clean = (text || '').trim();
    if (!clean || loading) return;
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: clean }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/terapeutia/chat`, {
        session_id: sessionId,
        message: clean,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
      if (res.data.suggest_cotizar) setSuggestCotizar(true);
    } catch (e) {
      setError('No pudimos procesar tu mensaje. Intenta nuevamente.');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Tuvimos un inconveniente técnico. Mientras tanto, puedes solicitar una [cotización gratis](/cotizar) o [explorar centros](/centros).' },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }

  function handleQuickPrompt(label) {
    sendPrompt(label);
  }

  function handleSubmit(e) {
    e.preventDefault();
    sendPrompt(input);
  }

  function handleReset() {
    setSessionId(newSessionId());
    setMessages([{ role: 'assistant', content: WELCOME }]);
    setSuggestCotizar(false);
    setError(null);
  }

  const heroImage = `${API.replace(/\/api$/, '')}/api/terapeutia/hero-image`;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* HERO */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Nueva herramienta gratuita
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
              <span className="text-white">TerapeutIA</span><br />
              <span className="text-emerald-400 text-3xl sm:text-4xl lg:text-5xl">IA especializada en adicciones</span>
            </h1>
            <p className="text-lg text-slate-300 mb-6 max-w-xl">
              Conversa con una inteligencia artificial enfocada <strong>solo en adicciones, tratamientos y rehabilitación</strong>. Respuestas con fuentes verificadas, sin moralismos, en español.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">
                <ShieldCheck className="w-3 h-3 text-emerald-400" /> Confidencial
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">
                <BookOpenCheck className="w-3 h-3 text-emerald-400" /> Fuentes verificadas
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">
                <Stethoscope className="w-3 h-3 text-emerald-400" /> Foco clínico
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                data-testid="hero-cta-cotizar"
              >
                <Link to="/cotizar">
                  Cotizar tratamiento gratis <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <a
                href="#chat"
                className="inline-flex items-center px-5 py-2.5 rounded-md border border-white/20 text-white hover:bg-white/10 text-sm font-medium"
                data-testid="hero-cta-start"
              >
                Iniciar conversación
              </a>
            </div>

            {/* Disclaimer destacado */}
            <div className="mt-7 flex items-start gap-3 p-3.5 rounded-lg bg-amber-500/10 border border-amber-400/30">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-100 leading-relaxed">
                <strong>TerapeutIA no reemplaza a un/a terapeuta y no entrega diagnósticos clínicos.</strong> Es una herramienta orientativa basada en evidencia. Para evaluación profesional, consulta nuestro <Link to="/profesionales" className="underline">directorio de profesionales</Link> o <Link to="/centros" className="underline">centros verificados</Link>.
              </p>
            </div>
          </div>

          <div className="hidden md:block">
            <div className="relative max-w-[420px] mx-auto">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 blur-3xl rounded-full" />
              <img
                src={heroImage}
                alt="Robot médico empático — TerapeutIA"
                className="relative w-full h-auto rounded-3xl shadow-2xl ring-1 ring-white/10"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CHAT */}
      <section id="chat" className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Chat header */}
            <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow">
                <MessageSquareHeart className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-900 text-base">TerapeutIA</h2>
                <p className="text-xs text-slate-500">Asistente especializada · solo adicciones · powered by Claude</p>
              </div>
              <button
                onClick={handleReset}
                className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
                data-testid="chat-reset"
                title="Nueva conversación"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reiniciar
              </button>
            </div>

            {/* Quick prompts */}
            <div className="px-5 pt-4 pb-2 border-b border-slate-100 bg-white">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">Preguntas frecuentes</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => handleQuickPrompt(q.label)}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 border border-slate-200 text-xs text-slate-700 transition-colors disabled:opacity-50"
                    data-testid={`quick-prompt-${q.id}`}
                  >
                    <span aria-hidden>{q.icon}</span>
                    {q.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollerRef}
              className="px-5 py-6 h-[480px] sm:h-[520px] overflow-y-auto bg-slate-50"
              data-testid="chat-messages"
            >
              <div className="space-y-4">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[88%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-emerald-600 text-white rounded-br-md whitespace-pre-wrap'
                          : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm space-y-0.5'
                      }`}
                      data-testid={`msg-${m.role}-${i}`}
                    >
                      {renderMessage(m.content)}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                    </div>
                  </div>
                )}
                {error && (
                  <div className="text-center text-xs text-red-600">{error}</div>
                )}
              </div>
            </div>

            {/* Cotizar CTA tras 4 mensajes */}
            {suggestCotizar && (
              <div className="px-5 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-t border-emerald-100" data-testid="cotizar-banner">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-slate-700">
                    <strong>¿Quieres una recomendación real?</strong> Cotiza gratis con centros que se ajusten a tu caso.
                  </p>
                  <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cta-cotizar-after-4">
                    <Link to="/cotizar">Cotizar gratis <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
                  </Button>
                </div>
              </div>
            )}

            {/* Input + sources */}
            <form onSubmit={handleSubmit} className="px-5 py-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu pregunta sobre adicciones…"
                  disabled={loading}
                  className="flex-1 text-slate-900 placeholder:text-slate-400 bg-white"
                  data-testid="chat-input"
                  maxLength={1000}
                />
                <Button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                <BookOpenCheck className="w-3 h-3 text-emerald-500" />
                <span className="font-semibold text-slate-600">Fuentes:</span>
                {SOURCES.map((s, i) => (
                  <span key={s} className="inline-flex items-center">
                    {s}{i < SOURCES.length - 1 && <span className="ml-2 text-slate-300">·</span>}
                  </span>
                ))}
              </div>
            </form>
          </div>

          {/* Sub-CTAs row */}
          <div className="grid sm:grid-cols-3 gap-3 mt-6">
            <Link to="/cotizar" className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all" data-testid="card-cotizar">
              <p className="text-sm font-semibold text-slate-900 mb-1">Cotizar tratamiento</p>
              <p className="text-xs text-slate-500">Recibe propuestas reales de centros adaptadas a tu presupuesto.</p>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-2 group-hover:gap-2 transition-all">Empezar <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/centros" className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all" data-testid="card-centros">
              <p className="text-sm font-semibold text-slate-900 mb-1">Buscar centros</p>
              <p className="text-xs text-slate-500">Residenciales, ambulatorios y online verificados en Chile.</p>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-2 group-hover:gap-2 transition-all">Ver directorio <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/herramientas" className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all" data-testid="card-herramientas">
              <p className="text-sm font-semibold text-slate-900 mb-1">Herramientas gratis</p>
              <p className="text-xs text-slate-500">Tests, calculadora, Reto 21 Días. Todo gratis y anónimo.</p>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-2 group-hover:gap-2 transition-all">Explorar <ArrowRight className="w-3 h-3" /></span>
            </Link>
          </div>

          {/* Footer disclaimer */}
          <div className="mt-8 p-4 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-600 leading-relaxed">
            <p>
              <strong className="text-slate-800">Aviso clínico:</strong> TerapeutIA es una herramienta de información orientativa, no constituye diagnóstico ni reemplaza la consulta con un/a profesional de salud. En caso de riesgo vital o ideación suicida, llama a <strong>Salud Responde 600 360 7777</strong> (24/7) o a <strong>*4141</strong> desde celular (línea de prevención del suicidio, Minsal).
            </p>
          </div>
        </div>
      </section>

      {/* FAQ visible (visible es requisito de Google para FAQ schema válido) */}
      <section className="py-12 sm:py-16 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Preguntas frecuentes sobre adicciones y tratamientos</h2>
            <p className="text-slate-600 text-sm">Respuestas claras basadas en evidencia. Puedes profundizar cualquiera con TerapeutIA.</p>
          </div>

          <div className="space-y-3" data-testid="terapeutia-faq">
            {FAQ_SCHEMA.mainEntity.map((q, idx) => (
              <details
                key={idx}
                className="group bg-slate-50 hover:bg-white border border-slate-200 hover:border-emerald-300 rounded-xl px-5 py-4 transition-colors"
                data-testid={`faq-item-${idx}`}
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <span className="text-base font-semibold text-slate-900 leading-snug">{q.name}</span>
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm font-bold group-open:rotate-45 transition-transform" aria-hidden>+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">{q.acceptedAnswer.text}</p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      const trigger = document.querySelector(`[data-testid="quick-prompt-${['soy-adicto','cuanto-cuesta','tipos','familiar'][idx]}"]`);
                      if (trigger) {
                        document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth' });
                        setTimeout(() => trigger.click(), 400);
                      }
                    }}
                    className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1"
                    data-testid={`faq-ask-${idx}`}
                  >
                    Preguntar a TerapeutIA <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </details>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Link to="/cotizar" data-testid="faq-cta-cotizar">Cotizar tratamiento gratis <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
